import type {
    ConditionalStringValueOption,
    FieldCondition,
    FieldOption,
    FieldOptionGroup,
    FormBuilderConfig,
    FormElementConfig,
    FormFieldConfig,
    FormValues,
} from '../../types/form-builder.types';
import { collectDependencies } from '../../utils/dependencies.utils';
import { isFieldConfig } from './field.utils';

class ConfigValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ConfigValidationError';
    }
}

// --- Condition validation ---

type ValidatorConfigMetadata =
    | { readonly type: 'required'; readonly condition?: boolean | FieldCondition }
    | { readonly type: 'pattern' }
    | { readonly type: 'custom'; readonly dependsOn?: readonly string[] };

interface ConfigReferenceContext {
    readonly elementIds: ReadonlySet<string>;
    readonly fieldIds: ReadonlySet<string>;
}

interface OwnedReferenceContext extends ConfigReferenceContext {
    readonly ownerId: string;
}

const validateFieldReference = (
    referencedId: string,
    referenceContext: string,
    context: OwnedReferenceContext
): void => {
    if (!context.elementIds.has(referencedId)) {
        throw new ConfigValidationError(
            `Element "${context.ownerId}" references unknown field "${referencedId}" in ${referenceContext}.`
        );
    }

    if (!context.fieldIds.has(referencedId)) {
        throw new ConfigValidationError(
            `Element "${context.ownerId}" references non-field "${referencedId}" in ${referenceContext}.`
        );
    }
};

const validateFieldCondition = <FormValuesType extends FormValues, FieldId extends string>(
    condition: FieldCondition<FormValuesType, FieldId>,
    context: OwnedReferenceContext
): void => {
    switch (condition.type) {
        case 'when':
        case 'whenNot':
        case 'whenHasValue': {
            validateFieldReference(condition.field, `${condition.type} condition`, context);
            break;
        }

        case 'whenAll':
        case 'whenSome': {
            if (condition.conditions.length === 0) {
                throw new ConfigValidationError(
                    `Element "${context.ownerId}" has an empty ${condition.type}() condition. Use true or false directly.`
                );
            }
            for (const child of condition.conditions) {
                validateFieldCondition(child, context);
            }
            break;
        }

        case 'custom': {
            for (const dep of condition.dependsOn) {
                validateFieldReference(dep, 'custom condition dependsOn', context);
            }
            break;
        }

        case 'whenData': {
            break;
        }
    }
};

const validateOptionalCondition = (
    condition: boolean | FieldCondition | undefined,
    context: OwnedReferenceContext
): void => {
    if (condition === undefined || typeof condition === 'boolean') {
        return;
    }
    validateFieldCondition(condition, context);
};

const isConditionalHelperText = (
    helperText: FormFieldConfig['helperText']
): helperText is readonly ConditionalStringValueOption[] => Array.isArray(helperText);

const validateDynamicHelperText = (fieldConfig: FormFieldConfig, context: OwnedReferenceContext): void => {
    if (fieldConfig.helperText === undefined || !isConditionalHelperText(fieldConfig.helperText)) {
        return;
    }

    for (const option of fieldConfig.helperText) {
        validateOptionalCondition(option.condition, context);
    }
};

// --- Validator validation ---

const validateValidators = (
    validators: readonly ValidatorConfigMetadata[],
    fieldConfig: FormFieldConfig,
    context: ConfigReferenceContext
): void => {
    const fieldContext: OwnedReferenceContext = { ...context, ownerId: fieldConfig.id };

    for (const validator of validators) {
        switch (validator.type) {
            case 'required': {
                if (validator.condition && typeof validator.condition !== 'boolean') {
                    validateFieldCondition(validator.condition, fieldContext);
                }
                break;
            }

            case 'pattern': {
                if (fieldConfig.type === 'checkbox' || fieldConfig.type === 'checkbox-group') {
                    throw new ConfigValidationError(
                        `Element "${fieldConfig.id}" has a pattern validator on a ${fieldConfig.type} field, which is invalid.`
                    );
                }
                break;
            }

            case 'custom': {
                if (validator.dependsOn) {
                    for (const dep of validator.dependsOn) {
                        validateFieldReference(dep, 'custom validator dependsOn', fieldContext);
                    }
                }
                break;
            }
        }
    }
};

// --- Default value validation ---

const isOptionGroup = (option: FieldOption | FieldOptionGroup): option is FieldOptionGroup => 'groupName' in option;

const validateDefaultValues = (config: FormBuilderConfig, fieldConfigs: ReadonlyMap<string, FormFieldConfig>): void => {
    if (!config.defaultValues) {
        return;
    }

    for (const [fieldId, value] of Object.entries(config.defaultValues)) {
        if (value === undefined) {
            continue;
        }

        const fieldConfig = fieldConfigs.get(fieldId);
        if (!fieldConfig) {
            throw new ConfigValidationError(`Default value provided for unknown field "${fieldId}".`);
        }

        switch (fieldConfig.type) {
            case 'checkbox': {
                if (typeof value !== 'boolean') {
                    throw new ConfigValidationError(
                        `Default value for checkbox "${fieldId}" must be a boolean, got ${typeof value}.`
                    );
                }
                break;
            }

            case 'checkbox-group': {
                if (!Array.isArray(value)) {
                    throw new ConfigValidationError(
                        `Default value for checkbox-group "${fieldId}" must be an array, got ${typeof value}.`
                    );
                }
                const validValues = new Set(fieldConfig.options.map((o) => o.value));
                for (const v of value) {
                    if (!validValues.has(v)) {
                        throw new ConfigValidationError(
                            `Default value "${String(v)}" for checkbox-group "${fieldId}" is not a valid option.`
                        );
                    }
                }
                break;
            }

            case 'radio': {
                if (typeof value !== 'string' && typeof value !== 'number') {
                    throw new ConfigValidationError(
                        `Default value for radio "${fieldId}" must be a string or number, got ${typeof value}.`
                    );
                }
                const validValues = new Set(fieldConfig.options.map((o) => o.value));
                if (value !== '' && !validValues.has(value)) {
                    throw new ConfigValidationError(
                        `Default value "${String(value)}" for radio "${fieldId}" is not a valid option.`
                    );
                }
                break;
            }

            case 'select': {
                if (typeof value !== 'string' && typeof value !== 'number') {
                    throw new ConfigValidationError(
                        `Default value for select "${fieldId}" must be a string or number, got ${typeof value}.`
                    );
                }
                const allOptions = fieldConfig.options.flatMap((o) => (isOptionGroup(o) ? o.options : [o]));
                const validValues = new Set(allOptions.map((o) => o.value));
                if (value !== '' && !validValues.has(value)) {
                    throw new ConfigValidationError(
                        `Default value "${String(value)}" for select "${fieldId}" is not a valid option.`
                    );
                }
                break;
            }

            case 'text':
            case 'number':
            case 'email':
            case 'textarea': {
                if (typeof value !== 'string' && typeof value !== 'number') {
                    throw new ConfigValidationError(
                        `Default value for ${fieldConfig.type} "${fieldId}" must be a string or number, got ${typeof value}.`
                    );
                }
                break;
            }
        }
    }
};

// --- Circular dependency detection ---

const detectCircularDependencies = (elements: readonly FormElementConfig[]): void => {
    const dependencies = new Map<string, Set<string>>();

    for (const element of elements) {
        const deps = new Set(collectDependencies(element));
        deps.delete(element.id);

        if (deps.size > 0) {
            dependencies.set(element.id, deps);
        }
    }

    const WHITE = 0;
    const GRAY = 1;
    const BLACK = 2;

    const colors = new Map<string, number>();
    for (const element of elements) {
        colors.set(element.id, WHITE);
    }

    const dfs = (nodeId: string, path: string[]): void => {
        colors.set(nodeId, GRAY);
        path.push(nodeId);

        const deps = dependencies.get(nodeId);
        if (deps) {
            for (const dep of deps) {
                const color = colors.get(dep);
                if (color === GRAY) {
                    const cycleStart = path.indexOf(dep);
                    const cycle = path.slice(cycleStart).concat(dep);
                    throw new ConfigValidationError(`Circular dependency detected: ${cycle.join(' \u2192 ')}.`);
                }
                if (color === WHITE) {
                    dfs(dep, path);
                }
            }
        }

        colors.set(nodeId, BLACK);
        path.pop();
    };

    for (const element of elements) {
        if (colors.get(element.id) === WHITE) {
            dfs(element.id, []);
        }
    }
};

// --- Main validation ---

export const validateConfig = (config: FormBuilderConfig): void => {
    const elementIds = new Set<string>();
    const fieldConfigs = new Map<string, FormFieldConfig>();

    for (const element of config.elements) {
        if (elementIds.has(element.id)) {
            throw new ConfigValidationError(`Duplicate element id: "${element.id}".`);
        }
        elementIds.add(element.id);
        if (isFieldConfig(element)) {
            fieldConfigs.set(element.id, element);
        }
    }

    const fieldIds = new Set(fieldConfigs.keys());
    const referenceContext: ConfigReferenceContext = { elementIds, fieldIds };

    for (const element of config.elements) {
        const elementReferenceContext: OwnedReferenceContext = { ...referenceContext, ownerId: element.id };

        validateOptionalCondition(element.showWhen, elementReferenceContext);
        validateOptionalCondition(element.hidden, elementReferenceContext);
        validateOptionalCondition(element.disabled, elementReferenceContext);

        if (isFieldConfig(element)) {
            validateDynamicHelperText(element, elementReferenceContext);

            if (element.validators) {
                validateValidators(element.validators, element, referenceContext);
            }
        }
    }

    detectCircularDependencies(config.elements);
    validateDefaultValues(config, fieldConfigs);
};
